import { inject, Injectable } from '@angular/core';
import { TableState, CommodityType } from '@ui/aos-lib';
import { Observable, map, take, tap } from 'rxjs';
import { mapTableSortToPayloadSort } from '../../../../shared/components/listing-page/helpers/map-sort-descriptor-to-sorting-state.helper';
import { ListingPageModel } from '../../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { TableData } from '../../../../shared/types/api-response.types';
import { DateTimeInterval } from '../../../../shared/types/datetime.types';
import { DevicesPayload } from '../../../equipment/devices/components/devices.models';
import { ConsumptionReadsTableFilters } from '../../customers/components/customer-details-consumption/customer-details-consumption.types';
import {
  unitOfMeasureConverter,
  UnitsOfMeasure,
  mapTableDataToSelectedUnit
} from '../../customers/components/customer-details-consumption/unit-of-measure/units-of-measure.utils';
import {
  ConsumptionReads,
  ConsumptionReadsTableFilter,
  GroupElectricConsumptionDtoFieldName
} from '../../shared/consumption.types';
import {
  selectTableState,
  selectConsumptionReads,
  selectConsumptionReadsWithUOM,
  selectCommodity,
  selectUnitOfMeasure,
  selectWidgetConsumptionsFilters
} from './consumption-analytics.reducer';
import { CustomerDetailsConsumptionApiService } from '../../customers/components/customer-details-consumption/customer-details-consumption.api.service';
import { Store } from '@ngrx/store';
import { ExportRow } from '../../../../shared/types/export.types';
import { TimezoneService } from '../../../../services/utility/timezone.service';
import { DateUtil } from '@portal/shared/utils/date-util';
import { GetTableDataItem } from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/meter-consumption-tab-content.types';
import { Commodity } from '@portal/shared/types/commodity.types';
import { FlowDirection } from '@portal/shared/types/filter.types';
import { ExportUtilsService } from 'src/app/services/utility/export-utils.service';
import { ExportFacadeService, ExportFileType } from 'src/app/services/utility/export-facade.service';

@Injectable({ providedIn: 'root' })
export class ConsumptionAnalyticsService {
  private readonly consumptionApiService = inject(CustomerDetailsConsumptionApiService);
  private readonly store = inject(Store);
  private readonly tzService = inject(TimezoneService);
  private readonly exportUtils = inject(ExportUtilsService);
  private readonly exportFacade = inject(ExportFacadeService);

  getTableData(filters: ConsumptionReadsTableFilters, tableState: TableState): Observable<TableData<ConsumptionReads>> {
    const { startDateUtc, endDateUtc, comparisonPeriod, reportGroupUIDs, interval } = filters;
    const { skip, take, sort } = tableState;
    const { start, end } = this.tzService.getDatepickerRange({
      start: startDateUtc,
      end: endDateUtc
    });
    const payload = {
      filters: [
        {
          filterType: ConsumptionReadsTableFilter.StartDateUtc,
          filteredValues: [DateUtil.toISOString(start)]
        },
        {
          filterType: ConsumptionReadsTableFilter.EndDateUtc,
          filteredValues: [DateUtil.addOneDayToString(end)]
        },
        {
          filterType: ConsumptionReadsTableFilter.Interval,
          filteredValues: interval ? [interval] : [DateTimeInterval.Day]
        },
        {
          filterType: ConsumptionReadsTableFilter.ComparisonPeriod,
          filteredValues: comparisonPeriod ? [comparisonPeriod] : []
        },
        {
          filterType: ConsumptionReadsTableFilter.ReportGroupUID,
          filteredValues: reportGroupUIDs
        }
      ],
      sort: {
        columnName: ConsumptionReadsTableFilter.DateTime,
        orderDirection: mapTableSortToPayloadSort(sort![0])
      },
      skip,
      take
    } as DevicesPayload;
    return this.consumptionApiService.getConsumptionReads(payload).pipe(
      map(reads => {
        const { currentPeriod, previousPeriod } = reads;
        const { items, totalCount } = currentPeriod;
        return {
          data: items.map((reads, index) => {
            return {
              ...reads,
              date: reads.date ? this.tzService.formatShort(reads.date) : reads.date,
              previousDate: previousPeriod
                ? this.tzService.formatShort(previousPeriod?.items[index]?.date ?? '')
                : null,
              previousConsumption: previousPeriod ? previousPeriod?.items[index]?.consumption : null
            };
          }),
          total: totalCount
        };
      })
    );
  }

  exportTableData(
    fileType: ExportFileType,
    visibility: Record<string, boolean> | null = null,
    order: Record<string, number> | null = null
  ): void {
    const filters = this.store.selectSignal(selectWidgetConsumptionsFilters)()!;
    const tableState = this.store.selectSignal(selectTableState)();
    const { selectedItem } = this.store.selectSignal(selectUnitOfMeasure)().model;
    const commodity = this.store.selectSignal(selectCommodity)();
    const payload = { ...filters, ...tableState };

    if (commodity === Commodity.Electricity) {
      const { total } = this.store.selectSignal(selectConsumptionReads)() as {
        total: number;
        data: GetTableDataItem[];
      };
      const uom = selectedItem.value !== 'Select' ? selectedItem.text : null;
      const flowDirections = filters.flowDirection ?? [];
      const withUom = (label: string): string => (uom ? `${label}(${uom})` : label);
      const directionTitleMap: Record<FlowDirection, string> = {
        [FlowDirection.Forward]: withUom('Forward'),
        [FlowDirection.Reverse]: withUom('Reverse'),
        [FlowDirection.Net]: withUom('Net'),
        [FlowDirection.Total]: withUom('Total')
      };

      const electricListingPageModel = {
        dataSource: {
          getAll: (ts: TableState) => this.getTableDataElectric(filters, ts),
          total
        },
        exportConfig: {
          fileName: 'Export-TotalConsumptionTable',
          mapData: ({ data }: TableData<GetTableDataItem>): ExportRow[] =>
            data.map(item => {
              const row: ExportRow = { date: { title: 'Date', value: item.date } };
              for (const dir of flowDirections) {
                row[dir.toLowerCase()] = {
                  title: directionTitleMap[dir],
                  value: item[dir.toLowerCase() as keyof GetTableDataItem] ?? ''
                };
              }
              return row;
            })
        }
      } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;

      this.exportUtils
        .getTableDataForExport(payload as DevicesPayload, electricListingPageModel as ListingPageModel, total)
        .pipe(
          tap(data =>
            this.exportFacade.exportRows(
              fileType as ExportFileType,
              data as ExportRow[],
              visibility,
              order,
              electricListingPageModel.exportConfig!.fileName!
            )
          ),
          take(1)
        )
        .subscribe();

      return;
    }

    const { total, data } = this.store.selectSignal(selectConsumptionReadsWithUOM)();
    const multiplier =
      commodity && commodity !== Commodity.Electricity
        ? unitOfMeasureConverter(
            commodity as CommodityType,
            (data[0] as ConsumptionReads).unitOfMeasure as UnitsOfMeasure,
            selectedItem.value as UnitsOfMeasure
          )
        : 1;
    const selectedUomItem = selectedItem?.value !== 'Select' ? selectedItem.text : null;

    const listingPageModel = {
      dataSource: {
        getAll: tableState => this.getTableData(filters, tableState as TableState),
        total: total
      },
      exportConfig: {
        fileName: 'Export-TotalConsumptionTable',
        mapData: ({ data }: TableData<ConsumptionReads>): ExportRow[] => {
          return mapTableDataToSelectedUnit(data, multiplier).map(consumptionWithUoM => ({
            date: {
              title: 'Date',
              value: consumptionWithUoM.date
            },
            consumption: {
              title: filters.comparisonPeriod
                ? `Consumption (Previous Consumption)${selectedUomItem ? `(${selectedUomItem})` : ''}`
                : `Consumption${selectedUomItem ? `(${selectedUomItem})` : ''}`,
              value: filters.comparisonPeriod
                ? `${consumptionWithUoM.consumption ?? 'n/a'}${
                    filters.comparisonPeriod ? ` (${consumptionWithUoM.previousConsumption ?? 'n/a'})` : ''
                  }`
                : `${consumptionWithUoM.consumption ?? ''}`
            }
          }));
        }
      }
    } as Pick<ListingPageModel, 'dataSource' | 'exportConfig'>;

    this.exportUtils
      .getTableDataForExport(payload as DevicesPayload, listingPageModel as ListingPageModel, total)
      .pipe(
        tap(data =>
          this.exportFacade.exportRows(
            fileType as ExportFileType,
            data as ExportRow[],
            visibility,
            order,
            listingPageModel.exportConfig!.fileName!
          )
        ),
        take(1)
      )
      .subscribe();
  }

  getTableDataElectric(
    filters: ConsumptionReadsTableFilters,
    tableState: TableState
  ): Observable<TableData<GetTableDataItem>> {
    const { startDateUtc, endDateUtc, reportGroupUIDs, flowDirection } = filters;
    const { skip, take, sort } = tableState;
    const { start, end } = this.tzService.getDatepickerRange({
      start: startDateUtc,
      end: endDateUtc
    });
    const payload = {
      filters: [
        {
          filterType: GroupElectricConsumptionDtoFieldName.DateTimeUTC,
          filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
        },
        {
          filterType: GroupElectricConsumptionDtoFieldName.ReportGroupUID,
          filteredValues: reportGroupUIDs
        },
        ...(flowDirection
          ? [
              {
                filterType: GroupElectricConsumptionDtoFieldName.FlowDirection,
                filteredValues: flowDirection
              }
            ]
          : [])
      ],
      sort: {
        columnName: GroupElectricConsumptionDtoFieldName.DateTimeUTC,
        orderDirection: mapTableSortToPayloadSort(sort![0])
      },
      skip,
      take
    } as DevicesPayload;

    return this.consumptionApiService.getConsumptionReadsElectric(payload).pipe(
      map(response => {
        const { items, totalCount } = response;
        return {
          data: items.map(item => {
            return {
              ...item,
              date: item.date ? this.tzService.formatShort(item.date) : item.date
            };
          }),
          total: totalCount
        };
      })
    );
  }
}
